/**
 * Checkout screen state (``/checkout``, reached from "Buy now") — addresses (+ the "set your location" gate for
 * pin-less addresses), the live cart re-priced for the address / coupon / coins, wallet as a payment source, COD or
 * Razorpay (mocked or real), order notes, and placing the order. Moved verbatim out of CheckoutPage so the classic
 * view and the Blinkit view share one implementation.
 *
 * Option (Blinkit view only; omitted = the original behaviour): onPlaced(orderId) — where to go once the order
 * exists (default: /orders/:id, replacing checkout).
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { useTenant } from "../../../contexts/TenantContext";
import { openRazorpayCheckout } from "../../../lib/razorpay";
import { hasRealCoords, needsLocationUpdate } from "../../../lib/addressLocation";
import { coinRedeemCap, coinRedeemMin } from "../../../lib/coinRedemption";
import { deliveryParams, deliveryPayload } from "../../../lib/lse";
import { codAllowedFor, isMultiShopCart, multiShopPayload, orderGroupPath, shopIssues } from "../../../lib/multiShop";

export function useCheckoutController(opts = {}) {
    const { state } = useLocation();
    const nav = useNavigate();
    const { user } = useAuth();
    const { lang, tenant } = useTenant();
    const [razorpayMock, setRazorpayMock] = useState(true);
    useEffect(() => {
        api.get("/payment-methods").then(({ data }) => setRazorpayMock(data.razorpay_mock !== false)).catch(() => {});
    }, []);

    const [addrList, setAddrList] = useState([]);
    const [selectedAddrId, setSelectedAddrId] = useState(null);
    const [showPicker, setShowPicker] = useState(false);
    const [showAddNew, setShowAddNew] = useState(false);

    const [payment, setPayment] = useState("COD");
    const [useWallet, setUseWallet] = useState(false);
    const [walletBalance, setWalletBalance] = useState(0);
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    // Read only by the Blinkit view: what the place-order flow is doing right now, and why the last attempt failed.
    const [payPhase, setPayPhase] = useState(null);      // null | "placing" | "paying" | "confirming"
    const [lastError, setLastError] = useState(null);
    const goToOrder = (orderId) => (opts.onPlaced ? opts.onPlaced(orderId) : nav(`/orders/${orderId}`, { replace: true }));
    // Live cart totals — refetched whenever the selected delivery address
    // / coupon / coin redemption changes so the "Place Order • ₹X" button
    // always reflects the actual distance-based delivery fee + discounts.
    const [liveCart, setLiveCart] = useState(null);
    // Blinkit view: the live cart request failed (the classic view keeps its last state) → retry by bumping this.
    const [liveCartError, setLiveCartError] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    // Location Service Engine delivery choice (stays null for tenants without it).
    const [delivery, setDelivery] = useState(null);
    // Multi-shop checkout (lib/multiShop.js) — only for a cart the server priced as spanning several shops. For
    // every other cart ``codAllowed`` is true, nothing is added to the payload and ``placed`` is ``goToOrder``.
    const requestRef = useRef(null);
    const multiShop = isMultiShopCart(liveCart);
    const codAllowed = codAllowedFor(liveCart);
    useEffect(() => {
        if (!codAllowed) setPayment((curr) => (curr === "COD" ? "RAZORPAY" : curr));
    }, [codAllowed]);
    const placed = (data) => {
        const group = orderGroupPath(data);
        if (group) nav(group, { replace: true, state: { justPlaced: true } });
        else goToOrder(data.id);
    };

    // Coupon: editable inline. Pre-filled from the cart's stored promo.
    const [couponInput, setCouponInput] = useState((state?.coupon_code || "").toUpperCase());
    const [couponDraft, setCouponDraft] = useState((state?.coupon_code || "").toUpperCase());
    const [showCouponPicker, setShowCouponPicker] = useState(false);
    const [availCoupons, setAvailCoupons] = useState(null);
    const [couponPickerBusy, setCouponPickerBusy] = useState(false);

    // Coins: balance + how many the customer wants to burn this order.
    const [coinsBalance, setCoinsBalance] = useState(0);
    const [coinsConfig, setCoinsConfig] = useState(null);
    const [coinsToRedeem, setCoinsToRedeem] = useState(0);

    useEffect(() => {
        api.get("/wallet").then(({ data }) => setWalletBalance(data?.wallet?.balance || 0)).catch(() => {});
        api.get("/customer/coins").then(({ data }) => {
            setCoinsBalance(Number(data?.balance) || 0);
            setCoinsConfig(data?.config || null);
        }).catch(() => {});
    }, []);

    const loadAddrs = async (autoSelect = true) => {
        try {
            const { data } = await api.get("/addresses");
            setAddrList(data);
            if (autoSelect && data.length > 0 && !selectedAddrId) {
                const def = data.find((a) => a.is_default) || data[0];
                setSelectedAddrId(def.id);
            }
            return data;
        } catch { return []; }
    };

    useEffect(() => { loadAddrs(); /* eslint-disable-next-line */ }, []);

    // Refetch the cart with the selected address + active discount inputs
    // so totals reflect both the delivery fee AND the discount stack.
    useEffect(() => {
        if (!selectedAddrId) return;
        let cancelled = false;
        (async () => {
            try {
                const params = new URLSearchParams({ address_id: selectedAddrId, remove_unavailable: "true" });
                if (couponDraft) params.set("coupon_code", couponDraft);
                if (coinsToRedeem > 0) params.set("coins_redeem", String(coinsToRedeem));
                deliveryParams(delivery, params);
                const { data } = await api.get(`/cart?${params.toString()}`);
                if (cancelled) return;
                setLiveCart(data);
                setLiveCartError(false);
                // If the server revoked a persisted coupon (cart no longer
                // meets min_cart_value), reset the local draft so the
                // checkout summary stops showing a "ghost" coupon line.
                if (data?.coupon_revoked) {
                    const { code, title, reason } = data.coupon_revoked;
                    setCouponInput("");
                    setCouponDraft("");
                    toast.error(`Coupon ${title || code} removed — ${reason}`);
                }
                if (data?.items_removed) {
                    toast.info(data.items_removed.message);
                    window.dispatchEvent(new Event("cart:update"));
                    if (!(data.items || []).length) nav("/cart", { replace: true });
                }
                // Coins slider was non-zero but the server returned 0
                // discount → clear the slider value too.
                if (coinsToRedeem > 0 && Number((data?.totals || {}).coins_discount || 0) === 0) {
                    setCoinsToRedeem(0);
                    toast.info("Coin redemption removed — cart below minimum");
                }
            } catch { /* keep stale state.grand fallback */ if (!cancelled) setLiveCartError(true); }
        })();
        return () => { cancelled = true; };
    }, [selectedAddrId, couponDraft, coinsToRedeem, nav, reloadKey, delivery]);

    const liveTotals = liveCart?.totals || {};
    const grandTotal = liveTotals.grand_total ?? state?.grand ?? 0;

    // Coin redemption derived cap: the lesser of the configured % of order
    // and the customer's balance. Driven by tenant.coins_config.
    const coinMax = coinRedeemCap(coinsConfig, coinsBalance, liveTotals);

    const coinMin = coinRedeemMin(coinsConfig);

    const handleAddNew = async (saved) => {
        await loadAddrs(false);
        setSelectedAddrId(saved.id);
        setShowAddNew(false);
        setShowPicker(false);
    };

    const selectedAddr = addrList.find((a) => a.id === selectedAddrId);

    // An address typed without a map pin is only locatable through its pincode. Ask the zone
    // check whether that pincode places it; when it doesn't, the customer must set their
    // location before ordering (see lib/addressLocation.js for the exact rule — a failed
    // check never blocks). Keyed on the pincode so switching addresses re-checks.
    const [addrZoneStatus, setAddrZoneStatus] = useState(undefined);
    const [showSetLocation, setShowSetLocation] = useState(false);
    useEffect(() => {
        setAddrZoneStatus(undefined);
        if (!selectedAddr || hasRealCoords(selectedAddr) || !selectedAddr.pincode) return undefined;
        let cancelled = false;
        api.get(`/zone-status?pincode=${encodeURIComponent(selectedAddr.pincode)}`)
            .then(({ data }) => { if (!cancelled) setAddrZoneStatus(data || null); })
            .catch(() => { if (!cancelled) setAddrZoneStatus(null); });
        return () => { cancelled = true; };
    }, [selectedAddr?.id, selectedAddr?.pincode, selectedAddr?.lat, selectedAddr?.lng]);   // eslint-disable-line react-hooks/exhaustive-deps
    const locationNeeded = needsLocationUpdate(selectedAddr, addrZoneStatus);

    const openCouponPicker = async () => {
        setShowCouponPicker(true);
        if (availCoupons !== null) return;     // already loaded
        setCouponPickerBusy(true);
        try {
            const rid = liveCart?.restaurant_id;
            const qs = new URLSearchParams();
            if (rid) qs.set("restaurant_id", rid);
            // Location Service Engine: area-targeted offers are listed only where they apply.
            if (liveCart?.lse && selectedAddr?.pincode) qs.set("pincode", selectedAddr.pincode);
            if (liveCart?.lse && selectedAddr?.lat != null) { qs.set("lat", selectedAddr.lat); qs.set("lng", selectedAddr.lng); }
            const url = qs.toString() ? `/coupons/available?${qs.toString()}` : "/coupons/available";
            const { data } = await api.get(url);
            setAvailCoupons(Array.isArray(data) ? data : []);
        } catch {
            setAvailCoupons([]);
        } finally {
            setCouponPickerBusy(false);
        }
    };

    const applyCouponFromPicker = (code) => {
        const up = String(code || "").toUpperCase();
        setCouponInput(up);
        setCouponDraft(up);
        setShowCouponPicker(false);
    };

    const placeOrder = async () => {
        if (!selectedAddr) {
            return toast.error("Please add a delivery address");
        }
        if (locationNeeded) {
            setShowSetLocation(true);
            return toast.error("Please set your delivery location on the map so we know where to deliver.");
        }
        const shopIssue = shopIssues(liveCart)[0];  // a shop in a multi-shop cart that can't take the order right now
        if (shopIssue) return toast.error(shopIssue.message);
        setBusy(true);
        setLastError(null);
        setPayPhase("placing");
        try {
            // strip non-address fields before sending
            const { line1, line2, city, state: st, pincode, lat, lng, landmark } = selectedAddr;
            const payload = {
                delivery_address: { line1, line2, city, state: st, pincode, lat, lng, landmark },
                payment_method: payment,
                coupon_code: couponDraft || undefined,
                coins_redeem: coinsToRedeem || 0,
                use_wallet: useWallet,
                notes,
                ...deliveryPayload(delivery),
                ...multiShopPayload(requestRef, liveCart),
            };

            if (payment === "RAZORPAY" && !razorpayMock) {
                // Real Razorpay flow: server creates the order first, we
                // open Checkout.js against it, then forward the payment
                // proof so the server can verify it before trusting "paid".
                const { data: rzpOrder } = await api.post("/payments/razorpay/create-order", payload);
                setPayPhase("paying");
                const proof = await openRazorpayCheckout({
                    keyId: rzpOrder.key_id,
                    orderId: rzpOrder.razorpay_order_id,
                    amount: rzpOrder.amount,
                    name: tenant?.name,
                    description: "Order payment",
                    themeColor: tenant?.branding?.custom_primary_hex,
                    prefill: { name: user?.first_name, contact: user?.mobile_number },
                });
                setPayPhase("confirming");
                const { data } = await api.post("/orders/checkout", { ...payload, ...proof });
                window.dispatchEvent(new Event("cart:update"));
                toast.success("Payment successful — order placed!");
                placed(data);
                return;
            }

            const { data } = await api.post("/orders/checkout", payload);
            window.dispatchEvent(new Event("cart:update"));
            toast.success("Order placed!");
            placed(data);
        } catch (e) {
            // A dismissed/failed Razorpay sheet rejects with a plain
            // Error (no ``response``) — no order was created in that case.
            const msg = e?.response?.data?.detail || e?.message || "Failed";
            toast.error(msg);
            setLastError(typeof msg === "string" ? msg : "Failed");
            if (multiShop && e?.response?.status === 409) {
                // The server refused or changed the cart (an item sold out, a shop left it): show it as it is now.
                window.dispatchEvent(new Event("cart:update"));
                setReloadKey((k) => k + 1);
            }
            // Server auto-clears orphan carts at checkout. If we hit
            // that path, dispatch the cart-refresh event + bounce back
            // to /cart so the user sees the empty state and can re-add.
            if (/shop is no longer available|has been removed by the store owner|not available on your storefront|removed from your cart/i.test(msg)) {
                window.dispatchEvent(new Event("cart:update"));
                nav("/cart", { replace: true });
            }
        } finally {
            setBusy(false);
            setPayPhase(null);
        }
    };

    return {
        lang, tenant, razorpayMock, codAllowed, addrList, selectedAddrId, setSelectedAddrId, showPicker, setShowPicker, showAddNew,
        setShowAddNew, payment, setPayment, useWallet, setUseWallet, walletBalance, notes, setNotes, busy, payPhase,
        liveCartError, retryLiveCart: () => setReloadKey((k) => k + 1),
        lastError, setLastError, liveCart, couponInput, setCouponInput, couponDraft, setCouponDraft, showCouponPicker,
        setShowCouponPicker, availCoupons, couponPickerBusy, coinsBalance, coinsConfig, coinsToRedeem, setCoinsToRedeem,
        loadAddrs, liveTotals, grandTotal, coinMax, coinMin, handleAddNew, selectedAddr, addrZoneStatus, showSetLocation,
        setShowSetLocation, locationNeeded, openCouponPicker, applyCouponFromPicker, placeOrder,
        // Location Service Engine (present only for tenants with it on)
        lse: liveCart?.lse || null, delivery, chooseDelivery: setDelivery,
    };
}
